import React, { useState, useEffect } from 'react';
import { getChildAIInsights } from '../../services/auth';
import { storage } from '../../services/storage';

interface AIInsightsData {
  days: number;
  total_ai_uses: number;
  usage_summary: {
    by_feature: Record<string, number>;
    by_week: Record<string, Record<string, number>>;
  };
  struggle_stories: Array<{
    lo_code: string;
    lo_description: string;
    unit: number;
    ai_uses: number;
    by_feature: Record<string, number>;
    before_accuracy: number | null;
    latest_accuracy: number | null;
    status: 'breakthrough' | 'still_struggling';
  }>;
  independence_trend: Array<{
    week: string;
    questions_answered: number;
    ai_uses: number;
    ai_rate: number;
  }>;
  feature_effectiveness: Array<{
    feature_type: string;
    total_uses: number;
    correct_after: number;
    effectiveness_rate: number;
  }>;
}

const PERIOD_OPTIONS = [
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

const FEATURE_LABELS: Record<string, string> = {
  explain: 'Explanations',
  worked_example: 'Worked Examples',
  dialogue: 'Dialogue',
};

const FEATURE_SHORT: Record<string, string> = {
  explain: 'Explain',
  worked_example: 'Worked Ex.',
  dialogue: 'Dialogue',
};

export const AIInsightsPanel: React.FC<{ studentId: string }> = ({ studentId }) => {
  const [data, setData] = useState<AIInsightsData | null>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTrustMessage, setShowTrustMessage] = useState(!storage.hasSeenTip('ai_insights_trust'));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getChildAIInsights(studentId, days)
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) setError('Failed to load AI insights'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId, days]);

  if (loading) {
    return <div className="ai-insights-loading"><div className="loading-spinner" /><p>Loading AI insights...</p></div>;
  }
  if (error) {
    return <div className="ai-insights-error"><p>{error}</p></div>;
  }
  if (!data || data.total_ai_uses === 0) {
    return (
      <div className="ai-insights-empty">
        <h3>No AI Usage Yet</h3>
        <p>Your child hasn't used any AI tutor features during this period. When they use explanations, worked examples, or the dialogue tutor, you'll see insights here about how those tools are helping them learn.</p>
      </div>
    );
  }

  const { usage_summary, struggle_stories, independence_trend, feature_effectiveness } = data;
  const features = ['explain', 'worked_example', 'dialogue'];

  // Current and previous week AI rate for summary
  const lastWeek = independence_trend.length > 0 ? independence_trend[independence_trend.length - 1] : null;
  const prevWeek = independence_trend.length > 1 ? independence_trend[independence_trend.length - 2] : null;

  return (
    <div className="ai-insights-panel">
      {/* Trust message for first-time viewers */}
      {showTrustMessage && (
        <div className="ai-insights-trust-message">
          <button
            className="trust-close"
            onClick={() => {
              storage.markTipSeen('ai_insights_trust');
              setShowTrustMessage(false);
            }}
          >
            &times;
          </button>
          <p>
            Every conversation your child has with the AI tutor is logged here.
            The tutor only discusses math and guides thinking without giving answers.
          </p>
        </div>
      )}

      {/* Period selector */}
      <div className="ai-insights-period">
        <span>Period:</span>
        {PERIOD_OPTIONS.map((opt) => (
          <button
            key={opt.days}
            className={`period-btn ${days === opt.days ? 'active' : ''}`}
            onClick={() => setDays(opt.days)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Usage Overview */}
      <div className="ai-insights-section">
        <h3>AI Usage Overview</h3>
        <div className="usage-cards">
          {features.map((f) => (
            <div key={f} className="usage-card">
              <div className="usage-count">{usage_summary.by_feature[f] || 0}</div>
              <div className="usage-label">{FEATURE_SHORT[f]}</div>
            </div>
          ))}
        </div>
        <p className="usage-total">{data.total_ai_uses} total AI uses in the last {days} days</p>
      </div>

      {/* Independence Trend */}
      <div className="ai-insights-section">
        <h3>Independence Trend</h3>
        {lastWeek && (
          <p className="trend-summary">
            AI usage: {lastWeek.ai_rate}% this week
            {prevWeek ? ` (was ${prevWeek.ai_rate}%)` : ''}
          </p>
        )}
        <div className="trend-bars">
          {independence_trend.map((wk) => {
            const weekLabel = new Date(wk.week).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
            return (
              <div key={wk.week} className="trend-bar-row">
                <div className="trend-bar-fill" style={{ width: `${Math.min(wk.ai_rate, 100)}%` }} />
                <span className="trend-bar-label">
                  {weekLabel} ({wk.ai_rate}% — {wk.ai_uses} AI / {wk.questions_answered} Qs)
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Struggle Stories */}
      <div className="ai-insights-section">
        <h3>Struggle Stories</h3>
        {struggle_stories.length === 0 ? (
          <p>No learning objectives with 3+ AI uses yet.</p>
        ) : (
          <div className="struggle-list">
            {struggle_stories.map((s) => (
              <div key={s.lo_code} className={`struggle-item ${s.status}`}>
                <div className="struggle-header">
                  <span className="struggle-icon">{s.status === 'breakthrough' ? '\u2605' : '\u26A0'}</span>
                  <span className="struggle-title">{s.lo_description} (Unit {s.unit})</span>
                </div>
                <div className="struggle-detail">
                  {s.ai_uses} AI uses
                  {s.status === 'breakthrough'
                    ? ` \u2192 Breakthrough! ${s.before_accuracy ?? '?'}% \u2192 ${s.latest_accuracy}%`
                    : ` \u2192 Still working (${s.latest_accuracy ?? '?'}%)`}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Feature Effectiveness */}
      <div className="ai-insights-section">
        <h3>Feature Effectiveness</h3>
        {feature_effectiveness.length === 0 ? (
          <p>Not enough data to measure effectiveness yet.</p>
        ) : (
          <div className="effectiveness-list">
            {feature_effectiveness
              .sort((a, b) => b.effectiveness_rate - a.effectiveness_rate)
              .map((fe) => (
                <div key={fe.feature_type} className="effectiveness-row">
                  <span className="effectiveness-label">
                    {FEATURE_LABELS[fe.feature_type] || fe.feature_type}
                  </span>
                  <span className="effectiveness-pct">{fe.effectiveness_rate}%</span>
                  <div className="effectiveness-bar">
                    <div
                      className="effectiveness-bar-fill"
                      style={{ width: `${fe.effectiveness_rate}%` }}
                    />
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};
